import React, { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Text,
  Badge,
  Spinner,
  Center,
  Button,
  Checkbox,
  Icon,
} from '@chakra-ui/react';
import {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
} from '@/components/ui/menu';
import { DataTable, dataTableButtonStyles } from '@/components/data-table';
import { type ColumnDef } from '@tanstack/react-table';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaChevronDown,
  FaEllipsisV,
  FaCopy,
  FaRegCopy,
} from 'react-icons/fa';
import { format } from 'date-fns';
import { useGetOrganizations } from '../organizations.hook';
import { Organization, GetOrganizationsParams } from '../organizations.types';
import { toaster } from '@/components/ui/toaster';

interface OrganizationListProps {
  onEdit?: (organization: Organization) => void;
  onDelete?: (organization: Organization) => void;
  onView?: (organization: Organization) => void;
  onCreate?: () => void;
  onSelect?: (organization: Organization) => void;
  selectedOrganizationId?: string;
}

export const OrganizationList = ({
  onEdit,
  onDelete,
  onView,
  onSelect,
  selectedOrganizationId,
}: OrganizationListProps) => {
  const [params, setParams] = useState<GetOrganizationsParams>({
    page: 1,
    perPage: 25,
    sort: 'name',
    order: 'ASC' as const,
    filter: {},
  });

  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>(
    '',
  );
  const [globalFilter, setGlobalFilter] = useState('');

  const { data, isLoading, isError, error } = useGetOrganizations(params);

  // Update params when filters or search change
  React.useEffect(() => {
    const newFilter: Record<string, any> = {};

    // Add status filter if set
    // if (statusFilter) {
    //   newFilter.status = statusFilter;
    // }

    // Add name search filter if set
    if (globalFilter) {
      newFilter.name = { _like: globalFilter };
    }

    setParams((prev) => ({
      ...prev,
      page: 1,
      filter: newFilter,
    }));
  }, [statusFilter, globalFilter]);

  // Helper functions
  const getStatusColor = (status: string): string => {
    return status === 'active' ? 'green' : 'gray';
  };

  const handleEdit = (organization: Organization) => {
    onEdit?.(organization);
  };

  const handleDelete = (organization: Organization) => {
    onDelete?.(organization);
  };

  const handleView = (organization: Organization) => {
    onView?.(organization);
  };

  const handleSelect = (organization: Organization) => {
    console.log('Organization selected:', organization.name, organization.id);
    onSelect?.(organization);
  };

  const handleCopy = (id: string) => {
    navigator.clipboard.writeText(id);
  };

  // Define columns
  const columns = useMemo<ColumnDef<Organization>[]>(
    () => [
      // Selection column
      {
        id: 'select',
        header: () => (
          <Checkbox.Root checked={false} disabled={true}>
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        ),
        cell: ({ row }) => {
          const isSelected = selectedOrganizationId === row.original.id;
          return (
            <Checkbox.Root
              checked={isSelected}
              onCheckedChange={(details) => {
                if (details.checked && onSelect) {
                  handleSelect(row.original);
                }
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          );
        },
        enableSorting: false,
        enableHiding: false,
        enableResizing: true,
        minSize: 50,
        maxSize: 80,
      },
      // Name column
      {
        accessorKey: 'name',
        header: 'NAME',
        cell: ({ row }) => {
          const isSelected = selectedOrganizationId === row.original.id;
          return (
            <Box
              cursor={onSelect ? 'pointer' : 'default'}
              onClick={() => {
                if (onSelect) {
                  console.log('Clicking on organization:', row.original.name);
                  handleSelect(row.original);
                }
              }}
              bg={isSelected ? 'blue.50' : 'transparent'}
              px={2}
              py={1}
              borderRadius="md"
              transition="all 0.2s"
              _hover={onSelect ? { bg: 'blue.100' } : {}}
              width="100%"
            >
              <Text
                fontWeight="semibold"
                color={isSelected ? 'blue.600' : 'gray.900'}
                _hover={onSelect ? { color: 'blue.600' } : {}}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                title={row.original.name}
              >
                {row.original.name}
              </Text>
            </Box>
          );
        },
        minSize: 120,
        enableResizing: true,
      },
      // Email column
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.id || '-'}
          >
            {row.original.id || '-'}
            <Badge
              colorScheme={getStatusColor(row.original.status)}
              onClick={() => handleCopy(row.original.id)}
              textTransform="capitalize"
              cursor="pointer"
              ml={2}
            >
              <Icon as={FaRegCopy} />
            </Badge>
          </Text>
        ),
        minSize: 80,
        enableResizing: true,
      },
      {
        accessorKey: 'email',
        header: 'EMAIL',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.email || '-'}
          >
            {row.original.email || '-'}
          </Text>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // Status column
      {
        accessorKey: 'status',
        header: 'STATUS',
        cell: ({ row }) => (
          <Badge
            colorScheme={getStatusColor(row.original.status)}
            textTransform="capitalize"
          >
            {row.original.status}
          </Badge>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // City column
      {
        accessorKey: 'city',
        header: 'CITY',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.city || '-'}
          >
            {row.original.city || '-'}
          </Text>
        ),
        minSize: 100,
        enableResizing: true,
      },
      // Phone column
      {
        accessorKey: 'phone',
        header: 'PHONE',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.phone || '-'}
          >
            {row.original.phone || '-'}
          </Text>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // Website column
      {
        accessorKey: 'website',
        header: 'WEBSITE',
        cell: ({ row }) => {
          // we need check if the website match that example https://www.
          const website = row.original.website
            ? row.original.website?.startsWith('https://')
              ? row.original.website
              : `https://${row.original.website}`
            : '-';
          const link = row.original.website ? (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: 'blue' }}
            >
              {website}
            </a>
          ) : (
            '-'
          );
          return (
            <Text
              color="gray.700"
              fontSize="sm"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={row.original.website || '-'}
            >
              {link}
            </Text>
          );
        },
        minSize: 100,
        enableResizing: true,
      },
      // Pay Per Use column
      {
        accessorKey: 'pay_per_use',
        header: 'PAY PER USE',
        cell: ({ row }) => (
          <Badge
            colorScheme={row.original.pay_per_use ? 'purple' : 'gray'}
            variant="subtle"
            size="sm"
            textTransform="capitalize"
          >
            {row.original.pay_per_use ? 'Enabled' : 'Disabled'}
          </Badge>
        ),
        minSize: 90,
        enableResizing: true,
      },
      // Max Interactions Per Month column
      {
        accessorKey: 'max_interaction_per_month',
        header: 'MAX INTERACTIONS/MONTH',
        cell: ({ row }) => (
          <Text color="gray.700" fontSize="sm">
            {row.original.max_interaction_per_month || '-'}
          </Text>
        ),
        minSize: 100,
        enableResizing: true,
      },
      // Max Interactions Per Session column
      {
        accessorKey: 'max_interaction_per_session',
        header: 'MAX INTERACTIONS/SESSION',
        cell: ({ row }) => (
          <Text color="gray.700" fontSize="sm">
            {row.original.max_interaction_per_session || '-'}
          </Text>
        ),
        minSize: 110,
        enableResizing: true,
      },
      // Actions column
      {
        id: 'actions',
        header: 'ACTIONS',
        cell: ({ row }) => (
          <MenuRoot>
            <MenuTrigger asChild>
              <IconButton
                aria-label="More actions"
                size="sm"
                variant="ghost"
                borderRadius="xl"
                color="gray.500"
                _hover={{
                  bg: 'gray.100',
                  color: 'gray.700',
                }}
              >
                <FaEllipsisV />
              </IconButton>
            </MenuTrigger>
            <MenuContent>
              {onView && (
                <MenuItem value="view" onClick={() => handleView(row.original)}>
                  <FaEye />
                  View Organization
                </MenuItem>
              )}
              {onEdit && (
                <MenuItem value="edit" onClick={() => handleEdit(row.original)}>
                  <FaEdit />
                  Edit Organization
                </MenuItem>
              )}
              {onDelete && (
                <MenuItem
                  value="delete"
                  onClick={() => handleDelete(row.original)}
                >
                  <FaTrash />
                  Delete Organization
                </MenuItem>
              )}
            </MenuContent>
          </MenuRoot>
        ),
        enableSorting: false,
        enableResizing: false,
        minSize: 80,
        maxSize: 80,
      },
    ],
    [onEdit, onDelete, onView, onSelect, selectedOrganizationId],
  );

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage: number) => {
    setParams((prev) => ({ ...prev, page: 1, perPage: newPerPage }));
  };

  if (isLoading) {
    return (
      <Box p={6} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading organizations...</Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Center py={8}>
        <Text color="red.500">
          Error loading organizations: {error?.message || 'Unknown error'}
        </Text>
      </Center>
    );
  }

  // Status filter component
  const statusFilterComponent = (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button
          {...dataTableButtonStyles.outline}
          justifyContent="space-between"
          minW="140px"
        >
          Status: {statusFilter || 'All'}
          <FaChevronDown />
        </Button>
      </MenuTrigger>
      <MenuContent>
        <MenuItem value="" onClick={() => setStatusFilter('')}>
          All Statuses
        </MenuItem>
        <MenuItem value="active" onClick={() => setStatusFilter('active')}>
          Active
        </MenuItem>
        <MenuItem value="inactive" onClick={() => setStatusFilter('inactive')}>
          Inactive
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  );

  return (
    <DataTable
      data={data?.data || []}
      columns={columns}
      isLoading={isLoading}
      total={data?.pageInfo?.totalItems || 0}
      page={params.page}
      perPage={params.perPage}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
      searchPlaceholder="Search organizations..."
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      filters={statusFilterComponent}
      bulkActions={null}
      enableSelection={false}
      emptyMessage="No organizations found"
      selectedRowId={selectedOrganizationId}
      getRowId={(organization) => organization.id}
    />
  );
};
